import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

/**
 * HreflangTags component for international SEO
 * Adds hreflang link tags to indicate language/region targeting
 * 
 * Currently configured for:
 * - en-US: Primary English (United States)
 * - x-default: Fallback for unmatched languages
 * 
 * To add more languages in the future, add entries to the languages array
 */

interface Language {
  code: string;  // e.g., 'en-US', 'es-MX', 'fr-CA'
  baseUrl: string;
}

const languages: Language[] = [
  { code: 'en-US', baseUrl: 'https://checkfirstcare.com' },
  // Add future language versions here:
  // { code: 'es-MX', baseUrl: 'https://es.checkfirstcare.com' },
  // { code: 'fr-CA', baseUrl: 'https://fr.checkfirstcare.com' },
];

const defaultLanguage = 'en-US';
const defaultBaseUrl = 'https://checkfirstcare.com';

interface HreflangTagsProps {
  /** Override the current path if needed */
  path?: string;
}

export function HreflangTags({ path }: HreflangTagsProps) {
  const location = useLocation();
  const currentPath = path || location.pathname;

  return (
    <Helmet>
      {/* Language-specific hreflang tags */}
      {languages.map((lang) => (
        <link
          key={lang.code}
          rel="alternate"
          hrefLang={lang.code}
          href={`${lang.baseUrl}${currentPath}`}
        />
      ))}
      
      {/* x-default for users whose language doesn't match any specified */}
      <link
        rel="alternate"
        hrefLang="x-default"
        href={`${defaultBaseUrl}${currentPath}`}
      />
      
      {/* Content-Language meta tag */}
      <meta httpEquiv="content-language" content={defaultLanguage.split('-')[0]} />
    </Helmet>
  );
}

/**
 * Helper function to generate hreflang links for sitemap
 * Can be used when generating XML sitemap with hreflang alternates
 */
export function getHreflangLinks(path: string): Array<{ code: string; url: string }> {
  return [
    ...languages.map((lang) => ({
      code: lang.code,
      url: `${lang.baseUrl}${path}`,
    })),
    { code: 'x-default', url: `${defaultBaseUrl}${path}` },
  ];
}
